import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Grid,
  LoadingOverlay,
  Table,
  Text,
  TextInput,
  TextInputProps,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconArrowLeft, IconPrinter } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  repositoryDelivery,
  repositoryMdm,
  repositoryPos,
} from "../../_base/_const/_constVar";
import { MessageResponse } from "../../model/MessageResponse";
import {
  getProvinceSelectNew,
  getCommuneSelectNew,
} from "../../service/getSelectApi";
import styles from "./CreateServiceRepairForm.module.css";
import { useReactToPrint } from "react-to-print";
import PrinterReceiveTicket from "./PrinterServiceRepair";
import PrinterReturnCustomers from "../Warranty/ReceiveWarranty/PrinterReturnCustomers";

// Hiện tooltip giá trị khi hover, chỉ khi text dài hơn ô bị cắt
const OverflowTooltipInput = (props: TextInputProps & { value: string }) => {
  const [opened, setOpened] = useState(false);
  return (
    <Tooltip label={props.value} opened={opened} multiline maw={400} withArrow>
      <TextInput
        {...props}
        onMouseEnter={(e) =>
          setOpened(e.currentTarget.scrollWidth > e.currentTarget.clientWidth)
        }
        onMouseLeave={() => setOpened(false)}
      />
    </Tooltip>
  );
};

const ServiceRepairDetailView = ({
  id: idProp,
  onClose: onCloseProp,
}: {
  id?: any;
  onClose?: any;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = idProp ?? (location.state as any)?.id ?? searchParams.get("id");
  const onClose = onCloseProp ?? (() => navigate(-1));

  const [visible, { close, open }] = useDisclosure(false);
  const [formData, setFormData] = useState<any>(null);
  const [addressBranch, setAddressBranch] = useState<any>({
    addressBranch: "",
    nameBranch: "",
    companyCode: "",
  });
  const [dataPrint, setDataPrint] = useState<any>(null);
  const [printView, setPrintView] = useState<number | null>(null);
  const [inventoryList, setInventoryList] = useState<any[]>([]);
  const [itemPositionList, setItemPositionList] = useState<any[]>([]);

  const componentRef = React.useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    pageStyle: `@page { size:auto; margin: 5mm 0; }`,
  });

  const componentRefRC = React.useRef(null);
  const handlePrintRC = useReactToPrint({
    contentRef: componentRefRC,
    pageStyle: `@page { size:auto; margin: 5mm 0; }`,
  });

  const callApiGetData = async () => {
    open();
    try {
      const callApi = await repositoryMdm.get<MessageResponse<any>>(
        `/api/v1/ServiceRepair/detail-service-repair?id=${id}`,
      );
      if (callApi?.success && callApi?.data) {
        let detailData = { ...callApi.data };
        if (detailData.provinceId) {
          const provinces = await getProvinceSelectNew();
          const prov = provinces?.find(
            (p: any) => Number(p.id) === Number(detailData.provinceId),
          );
          if (prov) {
            detailData.provinceName = prov.name;
          }
          const communes = await getCommuneSelectNew(
            String(detailData.provinceId),
          );
          const comm = communes?.find(
            (c: any) => Number(c.value) === Number(detailData.communeId),
          );
          if (comm) {
            detailData.communeName = comm.text;
          }
        }
        setFormData(detailData);
        const branchRes = await repositoryDelivery.get<MessageResponse<any>>(
          `/api/v1/TblDmMasterOrg/get-all`,
        );
        if (branchRes?.success) {
          const branch = branchRes.data?.find(
            (item: any) => Number(item.id) === Number(detailData.branchId),
          );
          if (branch) {
            setAddressBranch({
              addressBranch: branch.address,
              nameBranch: branch.name,
              companyCode: branch.companyCode,
            });
          }
        }
      } else {
        onClose("");
      }
    } catch (error) {
      console.error(error);
    } finally {
      close();
    }
  };

  const callApiGetInventory = async () => {
    try {
      const res = await repositoryPos.get<MessageResponse<any[]>>(
        `/api/v1/TblDmInventory/get-all?checkAuth=true&select=true`,
      );
      if (res?.success) {
        setInventoryList(res.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const callApiGetItemPosition = async () => {
    try {
      const res = await repositoryDelivery.get<MessageResponse<any[]>>(
        `/api/v1/TblDmItemPosition/get-all`,
      );
      if (res?.success) {
        setItemPositionList(res.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    callApiGetInventory();
    callApiGetItemPosition();
  }, []);

  useEffect(() => {
    if (id) {
      callApiGetData();
    } else {
      onClose("");
    }
  }, [id]);

  const details: any[] = formData?.tblWarrantyReceiveTicketDetailModels || [];

  const inventoryText =
    inventoryList.find((i: any) => i.att1 === formData?.inventoryCode)?.text ||
    formData?.inventoryCode ||
    "";

  const itemPosition = itemPositionList.find(
    (i: any) => Number(i.id) === Number(formData?.idItemPosition),
  );
  const itemPositionText = itemPosition
    ? `${itemPosition.positionCode} - ${itemPosition.positionName}`
    : formData?.idItemPosition
      ? `Vị trí ${formData.idItemPosition}`
      : "";

  const statusLabel = (val: string) => {
    switch (val) {
      case "DA_NHAP":
        return "Đã nhập kho";
      case "DA_TRA_KHACH":
        return "Đã trả khách";
      case "TRA_MOT_PHAN":
        return "Trả một phần";
      case "TAO_MOI":
        return "Tạo mới";
      case "DA_TIEP_NHAN":
        return "Đã tiếp nhận";
      default:
        return val || "—";
    }
  };

  const statusColor = (val: string) => {
    switch (val) {
      case "DA_NHAP":
        return "green";
      case "DA_TRA_KHACH":
        return "teal";
      case "TRA_MOT_PHAN":
        return "orange";
      case "TAO_MOI":
        return "gray";
      default:
        return "blue";
    }
  };

  if (!formData) {
    return (
      <Box pos="relative" h="100vh">
        <LoadingOverlay visible={visible} />
      </Box>
    );
  }

  return (
    <Box style={{ position: "relative" }}>
      <LoadingOverlay visible={visible} />

      {/* ===== TOP BAR ===== */}
      <div className={styles.topBar}>
        <ActionIcon
          variant="subtle"
          size="lg"
          onClick={() => onClose("")}
          style={{ borderColor: "rgba(255,255,255,.22)", color: "#fff" }}
        >
          <IconArrowLeft size={16} />
        </ActionIcon>
        <div className={styles.topBarTitle}>
          <h1>Chi tiết phiếu tiếp nhận</h1>
        </div>
        <div className={styles.topBarSpacer} />
        <Badge
          size="lg"
          radius="sm"
          variant="filled"
          color={statusColor(formData.attribute6)}
        >
          {statusLabel(formData.attribute6)}
        </Badge>
        {(formData.attribute6 === "DA_TRA_KHACH" ||
          formData.attribute6 === "TRA_MOT_PHAN") && (
          <Button
            radius="md"
            size="sm"
            color="blue"
            variant="light"
            leftSection={<IconPrinter size={14} />}
            onClick={() => {
              handlePrintRC();
              setPrintView(2);
            }}
          >
            In phiếu xuất trả
          </Button>
        )}
        <Button
          radius="md"
          size="sm"
          color="teal"
          leftSection={<IconPrinter size={14} />}
          onClick={() => {
            handlePrint();
            setPrintView(1);
          }}
        >
          In phiếu tiếp nhận
        </Button>
      </div>

      {/* ===== META STRIP ===== */}
      <div className={styles.metaStrip}>
        <div className={styles.metaItem}>
          <span className={styles.metaKey}>Ngày nhận</span>
          <span className={styles.metaValue}>
            {formData.createDate
              ? new Date(formData.createDate).toLocaleString("vi-VN")
              : "—"}
          </span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaKey}>Người nhận</span>
          <span className={styles.metaValue}>
            {formData.createByName || "—"}
          </span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaKey}>Phòng ban</span>
          <span className={styles.metaValue}>{formData.depName || "—"}</span>
        </div>
        <div className={styles.metaItem} style={{ marginLeft: "auto" }}>
          <span className={styles.metaKey}>Mã phiếu</span>
          <span
            className={styles.metaValue}
            style={{ color: "#e03131", fontWeight: 700 }}
          >
            {formData.errorNumber || "—"}
          </span>
        </div>
      </div>

      {/* ===== MAIN LAYOUT ===== */}
      <div className={styles.mainWrap}>
        <div className={styles.mainStack}>
          <Grid>
            {/* --- Left: Khách hàng --- */}
            <Grid.Col span={6}>
              <section className={styles.panel} style={{ height: "100%" }}>
                <div className={styles.panelHead}>
                  <h2>Khách hàng</h2>
                </div>
                <div className={styles.panelBody}>
                  <Grid>
                    <Grid.Col span={4}>
                      <TextInput
                        label="Tên khách hàng"
                        size="sm"
                        value={formData.custMeeting || ""}
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                    <Grid.Col span={4}>
                      <TextInput
                        label="Số điện thoại"
                        size="sm"
                        value={formData.custPhone || ""}
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                    <Grid.Col span={4}>
                      <TextInput
                        label="Tỉnh / Thành phố"
                        size="sm"
                        value={formData.provinceName || ""}
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                  </Grid>
                  <Grid mt={4}>
                    <Grid.Col span={4}>
                      <TextInput
                        label="Phường / Xã"
                        size="sm"
                        value={formData.communeName || ""}
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                    <Grid.Col span={8}>
                      <TextInput
                        label="Địa chỉ cụ thể"
                        size="sm"
                        value={String(formData.additionalAddress || "")}
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                  </Grid>
                </div>
              </section>
            </Grid.Col>

            {/* --- Right: Thông tin xử lý --- */}
            <Grid.Col span={6}>
              <section className={styles.panel} style={{ height: "100%" }}>
                <div className={styles.panelHead}>
                  <h2>Thông tin xử lý</h2>
                  <span className={styles.panelNote}>
                    Thông tin lưu trữ máy sau khi nhận
                  </span>
                </div>
                <div className={styles.panelBody}>
                  <Grid>
                    <Grid.Col span={4}>
                      <TextInput
                        label="Kênh tiếp nhận"
                        size="sm"
                        value={
                          formData.channelName || formData.attribute15 || ""
                        }
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                    <Grid.Col span={4}>
                      <TextInput
                        label="Hình thức xử lý"
                        size="sm"
                        value={formData.processingMethodName || ""}
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                    <Grid.Col span={4}>
                      <TextInput
                        label="Nhóm khách hàng"
                        size="sm"
                        value={formData.nameCustomerGroup || ""}
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                  </Grid>
                  <Text fw={700} size="sm" mt="md" mb={4} c="dark">
                    Nhập kho tiếp nhận
                  </Text>
                  <Grid>
                    <Grid.Col span={4}>
                      <TextInput
                        label="Chi nhánh"
                        size="sm"
                        value={addressBranch.nameBranch || ""}
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                    <Grid.Col span={4}>
                      <OverflowTooltipInput
                        label="Kho"
                        size="sm"
                        value={inventoryText}
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                    <Grid.Col span={4}>
                      <OverflowTooltipInput
                        label="Vị trí kho"
                        size="sm"
                        value={itemPositionText}
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                  </Grid>
                  <Grid mt={4}>
                    <Grid.Col span={12}>
                      <TextInput
                        label="Ghi chú nội bộ"
                        size="sm"
                        value={formData.note || ""}
                        readOnly
                        variant="filled"
                      />
                    </Grid.Col>
                  </Grid>
                </div>
              </section>
            </Grid.Col>
          </Grid>

          {/* --- Panel: Thiết bị nhận --- */}
          <section className={styles.panel}>
            <div className={styles.panelBody}>
              <Box style={{ overflowX: "auto", paddingBottom: 16 }}>
                <Table
                  withTableBorder
                  withColumnBorders
                  verticalSpacing="sm"
                  striped
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ minWidth: 200, whiteSpace: "nowrap" }}>
                        Tên sản phẩm
                      </Table.Th>
                      <Table.Th style={{ minWidth: 120, whiteSpace: "nowrap" }}>
                        Mã SP
                      </Table.Th>
                      <Table.Th style={{ minWidth: 150, whiteSpace: "nowrap" }}>
                        Serial / IMEI
                      </Table.Th>
                      <Table.Th style={{ minWidth: 140, whiteSpace: "nowrap" }}>
                        Loại dịch vụ
                      </Table.Th>
                      <Table.Th style={{ minWidth: 120, whiteSpace: "nowrap" }}>
                        Số hoá đơn
                      </Table.Th>
                      <Table.Th style={{ minWidth: 80, whiteSpace: "nowrap" }}>
                        SL
                      </Table.Th>
                      <Table.Th style={{ minWidth: 250, whiteSpace: "nowrap" }}>
                        Tình trạng khách báo
                      </Table.Th>
                      <Table.Th style={{ minWidth: 150, whiteSpace: "nowrap" }}>
                        Trạng thái
                      </Table.Th>
                      <Table.Th style={{ minWidth: 200, whiteSpace: "nowrap" }}>
                        Phụ kiện nhận kèm
                      </Table.Th>
                      <Table.Th style={{ minWidth: 150, whiteSpace: "nowrap" }}>
                        Chi phí dự kiến
                      </Table.Th>
                      <Table.Th style={{ minWidth: 140, whiteSpace: "nowrap" }}>
                        Hẹn trả máy
                      </Table.Th>
                      <Table.Th style={{ minWidth: 90, whiteSpace: "nowrap" }}>
                        Số ngày
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {details.map((item: any, idx: number) => (
                      <Table.Tr key={item.id || idx}>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={item.itemName || ""}
                            readOnly
                            variant="filled"
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={item.itemCode || ""}
                            readOnly
                            variant="filled"
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={item.serial || ""}
                            readOnly
                            variant="filled"
                            styles={{ input: { fontFamily: "monospace" } }}
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={item.serviceType || ""}
                            readOnly
                            variant="filled"
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={item.orderCode || ""}
                            readOnly
                            variant="filled"
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={item.amount?.toString() || "1"}
                            readOnly
                            variant="filled"
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={item.note || ""}
                            readOnly
                            variant="filled"
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={
                              item.attribute6 === "TEST_OK"
                                ? "Test OK"
                                : item.attribute6 === "TEST_LOI"
                                  ? "Test lỗi"
                                  : item.attribute6 === "CHO_TEST"
                                    ? "Chờ test"
                                    : item.attribute6 || ""
                            }
                            readOnly
                            variant="filled"
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={item.acessory || ""}
                            readOnly
                            variant="filled"
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={item.attribute8 || ""}
                            readOnly
                            variant="filled"
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={
                              item.scheduleTime
                                ? new Date(
                                    item.scheduleTime,
                                  ).toLocaleDateString("vi-VN")
                                : ""
                            }
                            readOnly
                            variant="filled"
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            size="xs"
                            value={item.daysReturn?.toString() || "0"}
                            readOnly
                            variant="filled"
                          />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            </div>
          </section>
        </div>
      </div>

      {/* ===== Sticky Footer ===== */}
      <div className={styles.stickyFooter}>
        <div className={styles.stickyFooterLeft}>
          <div className={styles.railCount} style={{ marginBottom: 0 }}>
            <span className={styles.railCountNum}>
              {String(details.length).padStart(2, "0")}
            </span>
            <span className={styles.railCountLabel}>máy trên phiếu</span>
          </div>
        </div>
        <div className={styles.stickyFooterRight}>
          <Badge
            size="lg"
            radius="sm"
            variant="light"
            color={statusColor(formData.attribute6)}
          >
            {statusLabel(formData.attribute6)}
          </Badge>
        </div>
      </div>

      {/* ===== Print Components ===== */}
      {printView === 1 && (
        <Box display="none">
          <PrinterReceiveTicket
            innerRef={componentRef}
            dataPrint={{ ...formData, ...addressBranch }}
            dataPYCXL={{
              custMeeting: formData.custMeeting,
              custPhone: formData.custPhone,
              additionalAddress: formData.additionalAddress,
              meetingAddress: formData.meetingAddress,
              processingMethodName: formData.processingMethodName,
              channelName: formData.channelName,
              requestNum: formData.requestNum,
            }}
          />
        </Box>
      )}
      {printView === 2 && dataPrint && (
        <Box display="none">
          <PrinterReturnCustomers
            innerRef={componentRefRC}
            dataPrint={{ ...dataPrint, ...addressBranch }}
            dataPYCXL={{
              custMeeting: formData.custMeeting,
              custPhone: formData.custPhone,
              additionalAddress: formData.additionalAddress,
            }}
            isCheck={true}
          />
        </Box>
      )}
    </Box>
  );
};

export default ServiceRepairDetailView;
